import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { wpaCommerceHeaders, wpaLocalizedName, isWpaCommerceCustomerOrderAllowed, buildWpaCustomerSuspendedBlock } from './wpaCommerceFetch';
import WpaLayout, { WPA_LAYOUT_HEADER_HEIGHT } from './WpaLayout';
import { useWpaCustomer } from './WpaCustomerContext';
import WpaAddToCartModal from './WpaAddToCartModal';
import { addProductToCart } from './wpaCartStore';
import {
  isCommerceOutOfStock,
  isCatalogStatusActive,
  isCatalogOutOfStockStatus,
  isWpaListHiddenStopOrderingStatus,
  isWpaProductOrderable,
  formatWpaCatalogStatusLabel,
} from './wpaProductUtils';

/** Avoids SyntaxError when the server returns 403/500 with an empty or non-JSON body. */
async function readJsonOrNull(res) {
  const text = await res.text();
  if (!text || !String(text).trim()) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

const PRIMARY = '#4CAF50';
const DELETE_RED = '#e53935';
const PAGE_SIZE = 20;
const ORDER_APP_PRICE_CARD_SOURCE = 'Order App';
const SWIPE_DELETE_WIDTH = 72;
/** 預設略為露出左側刪除區，提示可右滑刪除 */
const SWIPE_DELETE_PEEK_PX = 6;

function canDeleteOrderAppRecommendPriceCard(product) {
  return (
    product?.priceCardSource === ORDER_APP_PRICE_CARD_SOURCE
    && Number(product?.priceCardUnitPrice) === 0
    && !!product?.priceCardId
  );
}

/** 底部固定列（padding + 按鈕）約略高度，供列表留白避免被遮住 */
const HOME_BOTTOM_BAR_RESERVE_PX = 88;

const styles = {
  /** 捲動時貼在頂欄下方，與綠色 header 一併維持可見 */
  homeTopSticky: {
    position: 'sticky',
    top: WPA_LAYOUT_HEADER_HEIGHT,
    zIndex: 999,
    background: '#fff',
    alignSelf: 'stretch',
  },
  bannerAspectWrap: {
    width: '100%',
    aspectRatio: '16 / 10',
    position: 'relative',
    background: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)',
    overflow: 'hidden',
  },
  bannerPlaceholderInner: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: 1,
    textAlign: 'center',
    padding: 16,
  },
  bannerSlideBtn: {
    position: 'absolute',
    inset: 0,
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    display: 'block',
    width: '100%',
    height: '100%',
  },
  bannerSlideImg: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
  },
  bannerDots: {
    display: 'flex',
    justifyContent: 'center',
    gap: 6,
    padding: '8px 0',
    background: '#fff',
  },
  bannerNavBtn: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(0, 0, 0, 0.38)',
    color: '#fff',
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    zIndex: 2,
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
  },
  bannerNavBtnLeft: { left: 8 },
  bannerNavBtnRight: { right: 8 },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#ccc',
  },
  dotActive: {
    background: PRIMARY,
  },
  sectionTitle: {
    padding: '16px 16px 8px',
    fontSize: 16,
    fontWeight: 600,
    color: '#333',
  },
  list: {
    paddingBottom: `calc(${HOME_BOTTOM_BAR_RESERVE_PX}px + env(safe-area-inset-bottom, 0px))`,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
  },
  productRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    gap: 12,
  },
  productImg: {
    width: 56,
    height: 56,
    borderRadius: 8,
    objectFit: 'cover',
    background: '#f5f5f5',
    flexShrink: 0,
  },
  productImgPlaceholder: {
    width: 56,
    height: 56,
    borderRadius: 8,
    background: '#f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ccc',
    fontSize: 20,
    flexShrink: 0,
  },
  productInfo: {
    flex: 1,
    minWidth: 0,
  },
  productName: {
    fontSize: 15,
    fontWeight: 500,
    color: '#333',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  productItemNo: {
    fontSize: 12,
    color: '#999',
    marginTop: 2,
  },
  priceRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginTop: 4,
    flexWrap: 'wrap',
  },
  productPrice: {
    fontSize: 16,
    fontWeight: 700,
    color: PRIMARY,
  },
  outOfStock: {
    fontSize: 12,
    color: '#e53935',
    fontWeight: 600,
  },
  catalogStatus: {
    fontSize: 12,
    color: '#e65100',
    fontWeight: 600,
  },
  addBtn: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    background: PRIMARY,
    color: '#fff',
    border: 'none',
    fontSize: 22,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 2px 4px rgba(76,175,80,0.3)',
  },
  addBtnDisabled: {
    background: '#ccc',
    boxShadow: 'none',
    cursor: 'not-allowed',
  },
  bottomBar: {
    position: 'fixed',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    maxWidth: 480,
    padding: '12px 16px',
    paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
    background: '#fff',
    borderTop: '1px solid #e0e0e0',
    zIndex: 900,
    boxSizing: 'border-box',
  },
  bottomBtn: {
    width: '100%',
    padding: '14px 0',
    background: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
  },
  loading: {
    textAlign: 'center',
    padding: 32,
    color: '#999',
  },
  loadMore: {
    textAlign: 'center',
    padding: 16,
    color: '#999',
    fontSize: 14,
  },
  toast: {
    position: 'fixed',
    bottom: `calc(${HOME_BOTTOM_BAR_RESERVE_PX}px + env(safe-area-inset-bottom, 0px))`,
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(0,0,0,0.75)',
    color: '#fff',
    padding: '8px 20px',
    borderRadius: 20,
    fontSize: 14,
    zIndex: 2000,
    maxWidth: '92vw',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    textAlign: 'center',
  },
  promoFullscreen: {
    position: 'fixed',
    inset: 0,
    zIndex: 5500,
    background: '#000',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  promoFullscreenImgWrap: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 8px 8px',
    boxSizing: 'border-box',
  },
  promoFullscreenImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  promoFullscreenClose: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 2,
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(0,0,0,0.45)',
    color: '#fff',
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  promoFullscreenBar: {
    flexShrink: 0,
    padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))',
    background: '#fff',
    borderTop: '1px solid #e0e0e0',
  },
  promoFullscreenBtn: {
    width: '100%',
    padding: '14px 0',
    background: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
  },
  promoFullscreenBtnDisabled: {
    background: '#ccc',
    cursor: 'not-allowed',
  },
  promoMutedBtn: {
    width: '100%',
    padding: '14px 0',
    background: '#f5f5f5',
    color: '#999',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 500,
    cursor: 'default',
    textAlign: 'center',
  },
  errorOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 4000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  errorBox: {
    background: '#fff',
    borderRadius: 12,
    padding: 20,
    maxWidth: 340,
    width: '100%',
    maxHeight: '70vh',
    overflow: 'auto',
  },
  errorTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#333',
    marginBottom: 12,
  },
  errorBody: {
    fontSize: 14,
    color: '#555',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  errorOk: {
    marginTop: 16,
    width: '100%',
    height: 44,
    border: 'none',
    borderRadius: 8,
    background: PRIMARY,
    color: '#fff',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
  },
  swipeRowWrap: {
    position: 'relative',
    overflow: 'hidden',
    borderBottom: '1px solid #f0f0f0',
  },
  swipeRowWrapDeletable: {
    borderLeft: `3px solid ${DELETE_RED}`,
  },
  swipeDeleteBtn: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: SWIPE_DELETE_WIDTH,
    background: DELETE_RED,
    color: '#fff',
    border: 'none',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  swipeDeleteBtnDisabled: {
    background: '#bdbdbd',
    cursor: 'not-allowed',
  },
  swipeRowContent: {
    background: '#fff',
  },
  confirmActions: {
    marginTop: 16,
    display: 'flex',
    gap: 10,
  },
  confirmCancelBtn: {
    flex: 1,
    padding: '12px 0',
    background: '#fff',
    color: '#555',
    border: '1px solid #ddd',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  confirmConfirmBtn: {
    flex: 1,
    padding: '12px 0',
    background: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  confirmConfirmDanger: {
    background: DELETE_RED,
  },
};

function WpaRecommendSwipeRow({
  product,
  swipeOpen,
  onSwipeOpen,
  onSwipeClose,
  deleting,
  onDelete,
  deleteLabel,
  children,
}) {
  const canDelete = canDeleteOrderAppRecommendPriceCard(product);
  const [dragOffset, setDragOffset] = useState(SWIPE_DELETE_PEEK_PX);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartX = useRef(null);
  const touchStartOffset = useRef(0);

  useEffect(() => {
    if (!swipeOpen) {
      setDragOffset(SWIPE_DELETE_PEEK_PX);
    }
  }, [swipeOpen]);

  const displayedOffset = swipeOpen
    ? SWIPE_DELETE_WIDTH
    : Math.max(SWIPE_DELETE_PEEK_PX, dragOffset);

  const handleTouchStart = (e) => {
    if (!canDelete || deleting) return;
    setIsDragging(true);
    touchStartX.current = e.touches[0]?.clientX ?? null;
    touchStartOffset.current = swipeOpen ? SWIPE_DELETE_WIDTH : displayedOffset;
  };

  const handleTouchMove = (e) => {
    if (!canDelete || deleting || touchStartX.current == null) return;
    const x = e.touches[0]?.clientX;
    if (x == null) return;
    const delta = x - touchStartX.current;
    const next = Math.max(
      SWIPE_DELETE_PEEK_PX,
      Math.min(SWIPE_DELETE_WIDTH, touchStartOffset.current + delta),
    );
    setDragOffset(next);
    if (next > SWIPE_DELETE_PEEK_PX && !swipeOpen) onSwipeOpen(product.id);
  };

  const handleTouchEnd = () => {
    if (!canDelete || deleting) return;
    touchStartX.current = null;
    setIsDragging(false);
    const finalOffset = swipeOpen ? SWIPE_DELETE_WIDTH : displayedOffset;
    if (finalOffset >= SWIPE_DELETE_WIDTH / 2) {
      onSwipeOpen(product.id);
      setDragOffset(SWIPE_DELETE_WIDTH);
    } else {
      onSwipeClose();
      setDragOffset(SWIPE_DELETE_PEEK_PX);
    }
  };

  const rowContent = (
    <div
      style={{
        ...styles.swipeRowContent,
        ...styles.productRow,
        transform: `translateX(${displayedOffset}px)`,
        transition: isDragging ? 'none' : 'transform 0.2s ease',
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {children}
    </div>
  );

  if (!canDelete) {
    return (
      <div style={styles.swipeRowWrap}>
        <div style={{ ...styles.swipeRowContent, ...styles.productRow }}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.swipeRowWrap, ...styles.swipeRowWrapDeletable }}>
      <button
        type="button"
        style={{
          ...styles.swipeDeleteBtn,
          ...(deleting ? styles.swipeDeleteBtnDisabled : {}),
        }}
        onClick={() => {
          if (deleting) return;
          onDelete(product);
        }}
        aria-disabled={deleting}
        aria-label={deleteLabel}
      >
        {deleting ? '…' : deleteLabel}
      </button>
      {rowContent}
    </div>
  );
}

export default function WpaHomePage() {
  const { t, currentLanguage } = useLanguage();
  const { customerSessionReady, currentCustomer } = useWpaCustomer();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [errorDialog, setErrorDialog] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalProduct, setModalProduct] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [promosLoading, setPromosLoading] = useState(true);
  const [promoIndex, setPromoIndex] = useState(0);
  const [fullscreenPromo, setFullscreenPromo] = useState(null);
  const touchStartX = useRef(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);
  const [openSwipeProductId, setOpenSwipeProductId] = useState(null);
  const [deletingPriceCardId, setDeletingPriceCardId] = useState(null);
  const [confirmDeleteProduct, setConfirmDeleteProduct] = useState(null);

  const fetchPromotions = useCallback(async () => {
    const token = localStorage.getItem('wpa_token');
    try {
      const params = new URLSearchParams();
      try {
        const cust = JSON.parse(localStorage.getItem('wpa_current_customer') || 'null');
        if (cust?.id) params.set('customerId', cust.id);
      } catch { /* ignore */ }
      const res = await fetch(`/api/wpa-commerce/home-promotions?${params}`, {
        headers: wpaCommerceHeaders(token, currentLanguage),
      });
      if (res.status === 401) {
        setPromosLoading(false);
        navigate(`/${companyId}/wpa/login`, { replace: true });
        return;
      }
      const json = await readJsonOrNull(res);
      if (!json) {
        setPromotions([]);
        return;
      }
      const list = Array.isArray(json) ? json : (json.data || json.items || []);
      setPromotions(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to fetch home promotions', err);
      setPromotions([]);
    } finally {
      setPromosLoading(false);
    }
  }, [navigate, companyId, currentLanguage]);

  const fetchRecommended = useCallback(async (pageNum, append = false) => {
    const token = localStorage.getItem('wpa_token');
    try {
      if (!append) setLoading(true);
      else setLoadingMore(true);

      const params = new URLSearchParams({
        page: String(pageNum),
        pageSize: String(PAGE_SIZE),
      });
      try {
        const cust = JSON.parse(localStorage.getItem('wpa_current_customer') || 'null');
        if (cust?.id) params.set('customerId', cust.id);
      } catch { /* ignore */ }
      const res = await fetch(`/api/wpa-commerce/home-recommended-products?${params}`, {
        headers: wpaCommerceHeaders(token, currentLanguage),
      });
      if (res.status === 401) {
        navigate(`/${companyId}/wpa/login`, { replace: true });
        return;
      }
      const json = await readJsonOrNull(res);
      if (!json) {
        if (!append) setProducts([]);
        setHasMore(false);
        return;
      }
      const raw = json.data || json.items || json;
      const all = Array.isArray(raw) ? raw : [];
      const items = all.filter((p) => !isWpaListHiddenStopOrderingStatus(p.status));
      const total = typeof json.total === 'number' ? json.total : items.length;
      setHasMore(pageNum * PAGE_SIZE < total);

      if (append) {
        setProducts((prev) => [...prev, ...items]);
      } else {
        setProducts(items);
      }
    } catch (err) {
      console.error('Failed to fetch home recommended products', err);
      if (!append) setProducts([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [navigate, companyId, currentLanguage]);

  useEffect(() => {
    if (!customerSessionReady) return;
    setPromosLoading(true);
    fetchPromotions();
  }, [fetchPromotions, customerSessionReady, currentCustomer?.id]);

  useEffect(() => {
    if (!customerSessionReady) return;
    setPage(1);
    setHasMore(true);
    fetchRecommended(1, false);
  }, [fetchRecommended, customerSessionReady, currentCustomer?.id]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchRecommended(nextPage, true);
      }
    }, { threshold: 0.1 });

    observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [hasMore, loading, loadingMore, page, fetchRecommended]);

  const visiblePromos = promotions.filter((p) => p && p.bannerUrl);

  useEffect(() => {
    setPromoIndex((i) => {
      if (!visiblePromos.length) return 0;
      return Math.min(i, visiblePromos.length - 1);
    });
  }, [visiblePromos.length]);

  useEffect(() => {
    if (visiblePromos.length <= 1) return undefined;
    const tmr = setInterval(() => {
      setPromoIndex((i) => (i + 1) % visiblePromos.length);
    }, 5500);
    return () => clearInterval(tmr);
  }, [visiblePromos.length]);

  const showToast = (msg, durationMs = 1500) => {
    setToast(msg);
    setTimeout(() => setToast(''), durationMs);
  };

  const blockIfCustomerSuspended = () => {
    const cust = currentCustomer;
    if (!cust || isWpaCommerceCustomerOrderAllowed(cust)) return false;
    setErrorDialog(buildWpaCustomerSuspendedBlock(cust, t));
    return true;
  };

  const openAddModal = (product) => {
    if (isCommerceOutOfStock(product.availability)) {
      showToast(t('wpa.common.cannotOrderOutOfStock'), 3800);
      return;
    }
    if (!isCatalogStatusActive(product.status)) {
      showToast(
        t('wpa.common.cannotOrderInactiveStatus', {
          status: formatWpaCatalogStatusLabel(product.status, t) || '-',
        }),
        3800,
      );
      return;
    }
    if (blockIfCustomerSuspended()) return;
    setModalProduct(product);
    setModalOpen(true);
  };

  const openPromoFullscreen = () => {
    const cur = visiblePromos[promoIndex];
    if (!cur) return;
    const src = cur.fullscreenUrl || cur.bannerUrl;
    if (!src) return;
    setFullscreenPromo(cur);
  };

  const handlePromoTouchStart = (e) => {
    if (visiblePromos.length <= 1) return;
    touchStartX.current = e.changedTouches[0]?.clientX ?? null;
  };

  const handlePromoTouchEnd = (e) => {
    if (visiblePromos.length <= 1 || touchStartX.current == null) return;
    const endX = e.changedTouches[0]?.clientX;
    if (endX == null) return;
    const dx = endX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx > 0) {
      setPromoIndex((i) => (i - 1 + visiblePromos.length) % visiblePromos.length);
    } else {
      setPromoIndex((i) => (i + 1) % visiblePromos.length);
    }
  };

  const handlePromoAddToCart = () => {
    const pr = fullscreenPromo?.product;
    if (!pr) return;
    if (isCommerceOutOfStock(pr.availability)) {
      showToast(t('wpa.common.cannotOrderOutOfStock'), 3800);
      return;
    }
    if (!isCatalogStatusActive(pr.status)) {
      showToast(
        t('wpa.common.cannotOrderInactiveStatus', { status: formatWpaCatalogStatusLabel(pr.status, t) || '-' }),
        3800,
      );
      return;
    }
    if (blockIfCustomerSuspended()) return;
    setFullscreenPromo(null);
    setModalProduct(pr);
    setModalOpen(true);
  };

  const finishAdd = (qty, createNew) => {
    if (!modalProduct) return;
    if (blockIfCustomerSuspended()) return;
    addProductToCart(modalProduct, qty, createNew, currentLanguage);
    setModalOpen(false);
    setModalProduct(null);
    const pn = wpaLocalizedName(modalProduct);
    if (createNew) {
      showToast(t('wpa.home.toastNewCart', { name: pn, qty }));
    } else {
      showToast(t('wpa.home.toastAddedLatest', { name: pn, qty }));
    }
  };

  const deleteRecommendPriceCard = async (product) => {
    if (deletingPriceCardId || !canDeleteOrderAppRecommendPriceCard(product)) return;

    let customer;
    try {
      customer = JSON.parse(localStorage.getItem('wpa_current_customer') || 'null');
    } catch {
      customer = null;
    }
    if (!customer?.id) {
      showToast(t('wpa.productList.recommendNeedCustomer'));
      return;
    }

    const token = localStorage.getItem('wpa_token');
    setDeletingPriceCardId(product.priceCardId);
    try {
      const params = new URLSearchParams({ customerId: customer.id });
      const res = await fetch(
        `/api/wpa-commerce/order-app-recommend-price-card/${product.priceCardId}?${params}`,
        {
          method: 'DELETE',
          headers: wpaCommerceHeaders(token, currentLanguage),
        },
      );
      const json = await readJsonOrNull(res);
      if (res.status === 401) {
        navigate(`/${companyId}/wpa/login`, { replace: true });
        return;
      }
      if (!res.ok) {
        const msg = json?.message || json?.error || t('wpa.home.recommendDeleteFailed');
        showToast(msg, 3200);
        return;
      }
      setProducts((prev) => prev.filter((p) => p.id !== product.id));
      setOpenSwipeProductId(null);
      showToast(t('wpa.home.recommendDeleted', { name: wpaLocalizedName(product) }));
    } catch {
      showToast(t('wpa.home.recommendDeleteFailed'), 3200);
    } finally {
      setDeletingPriceCardId(null);
    }
  };

  const requestDeleteRecommendPriceCard = (product) => {
    if (deletingPriceCardId || !canDeleteOrderAppRecommendPriceCard(product)) return;
    setConfirmDeleteProduct(product);
  };

  const bannerAwaitingSession = !customerSessionReady;
  const currentPromo = visiblePromos[promoIndex];
  const fullscreenSrc = fullscreenPromo
    ? (fullscreenPromo.fullscreenUrl || fullscreenPromo.bannerUrl)
    : null;
  const promoProduct = fullscreenPromo?.product;
  const canPromoCart = promoProduct && isWpaProductOrderable(promoProduct);

  return (
    <WpaLayout
      title={t('wpa.titles.home')}
      leadingMode={modalOpen || fullscreenPromo ? 'back' : 'menu'}
      onBack={
        fullscreenPromo
          ? () => setFullscreenPromo(null)
          : modalOpen
            ? () => { setModalOpen(false); setModalProduct(null); }
            : undefined
      }
    >
      <div style={styles.homeTopSticky}>
        {(promosLoading || bannerAwaitingSession) ? (
          <div style={styles.bannerAspectWrap}>
            <div style={styles.bannerPlaceholderInner}>
              <span style={{ fontSize: 14, fontWeight: 500 }}>{t('wpa.common.loading')}</span>
            </div>
          </div>
        ) : visiblePromos.length === 0 ? (
          <div style={styles.bannerAspectWrap}>
            <div style={styles.bannerPlaceholderInner}>
              <span>{t('wpa.home.banner')}</span>
            </div>
          </div>
        ) : (
          <>
            <div
              style={styles.bannerAspectWrap}
              onTouchStart={handlePromoTouchStart}
              onTouchEnd={handlePromoTouchEnd}
            >
              <button
                type="button"
                style={styles.bannerSlideBtn}
                onClick={openPromoFullscreen}
                aria-label={t('wpa.home.ariaFullscreenAd')}
              >
                {currentPromo?.bannerUrl ? (
                  <img src={currentPromo.bannerUrl} alt="" style={styles.bannerSlideImg} />
                ) : null}
              </button>
              {visiblePromos.length > 1 && (
                <>
                  <button
                    type="button"
                    style={{ ...styles.bannerNavBtn, ...styles.bannerNavBtnLeft }}
                    aria-label={t('wpa.home.promoPrev')}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPromoIndex((i) => (i - 1 + visiblePromos.length) % visiblePromos.length);
                    }}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    style={{ ...styles.bannerNavBtn, ...styles.bannerNavBtnRight }}
                    aria-label={t('wpa.home.promoNext')}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPromoIndex((i) => (i + 1) % visiblePromos.length);
                    }}
                  >
                    ›
                  </button>
                </>
              )}
            </div>
            <div style={styles.bannerDots} role="tablist" aria-label={t('wpa.home.promoCarouselDots')}>
              {visiblePromos.map((_, i) => (
                <button
                  key={visiblePromos[i].id ?? i}
                  type="button"
                  role="tab"
                  aria-selected={i === promoIndex}
                  onClick={() => setPromoIndex(i)}
                  style={{
                    ...styles.dot,
                    ...(i === promoIndex ? styles.dotActive : {}),
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div style={styles.sectionTitle}>{t('wpa.home.recommended')}</div>

      <div style={styles.list}>
        {(loading || bannerAwaitingSession) && <div style={styles.loading}>{t('wpa.common.loading')}</div>}
        {!loading && !bannerAwaitingSession && products.length === 0 && (
          <div style={styles.loading}>{t('wpa.common.noProducts')}</div>
        )}
        {products.map((p) => {
          const outStock = isCommerceOutOfStock(p.availability);
          const inactiveCatalog = !isCatalogStatusActive(p.status);
          const orderBlocked = outStock || inactiveCatalog;
          const catalogOos = inactiveCatalog && isCatalogOutOfStockStatus(p.status);
          const stockLabel = outStock && !catalogOos ? t('wpa.common.outOfStock') : null;
          const statusLabel = inactiveCatalog ? (formatWpaCatalogStatusLabel(p.status, t) || '-') : null;
          const pName = wpaLocalizedName(p);
          const deleting = deletingPriceCardId === p.priceCardId;
          return (
            <WpaRecommendSwipeRow
              key={p.id}
              product={p}
              swipeOpen={openSwipeProductId === p.id}
              onSwipeOpen={(id) => setOpenSwipeProductId(id)}
              onSwipeClose={() => setOpenSwipeProductId(null)}
              deleting={deleting}
              onDelete={requestDeleteRecommendPriceCard}
              deleteLabel={t('wpa.home.recommendDelete')}
            >
              {p.imageUrl ? (
                <img src={p.imageUrl} alt={pName} style={styles.productImg} />
              ) : (
                <div style={styles.productImgPlaceholder}>📦</div>
              )}
              <div style={styles.productInfo}>
                <div style={styles.productName}>{pName}</div>
                <div style={styles.productItemNo}>{p.itemNo}</div>
                <div style={styles.priceRow}>
                  {p.showPrice !== false && p.price != null ? (
                    <div style={styles.productPrice} translate="no">${Number(p.price).toFixed(2)}</div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#999' }}>{t('wpa.common.priceInquire')}</div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                    {stockLabel && <div style={styles.outOfStock}>{stockLabel}</div>}
                    {statusLabel && <div style={styles.catalogStatus}>{statusLabel}</div>}
                  </div>
                </div>
              </div>
              <button
                type="button"
                style={{ ...styles.addBtn, ...(orderBlocked ? styles.addBtnDisabled : {}) }}
                onClick={() => openAddModal(p)}
                aria-disabled={orderBlocked}
                aria-label={t('wpa.common.addToCartAria')}
              >
                +
              </button>
            </WpaRecommendSwipeRow>
          );
        })}
        {loadingMore && <div style={styles.loadMore}>{t('wpa.common.loadMore')}</div>}
        <div ref={sentinelRef} style={{ height: 1 }} />
      </div>

      <div style={styles.bottomBar}>
        <button type="button" style={styles.bottomBtn} onClick={() => navigate(`/${companyId}/wpa/categories`)}>
          {t('wpa.home.allCategoriesBtn')}
        </button>
      </div>

      {fullscreenSrc ? (
        <div
          style={styles.promoFullscreen}
          role="dialog"
          aria-modal="true"
          aria-label={t('wpa.home.ariaFullscreenAd')}
        >
          <button
            type="button"
            style={styles.promoFullscreenClose}
            onClick={() => setFullscreenPromo(null)}
            aria-label={t('wpa.home.promoClose')}
          >
            ×
          </button>
          <div style={styles.promoFullscreenImgWrap}>
            <img src={fullscreenSrc} alt="" style={styles.promoFullscreenImg} />
          </div>
          <div style={styles.promoFullscreenBar}>
            {!promoProduct ? (
              <div style={styles.promoMutedBtn}>{t('wpa.home.promoNoProduct')}</div>
            ) : !canPromoCart ? (
              <button
                type="button"
                style={{ ...styles.promoFullscreenBtn, ...styles.promoFullscreenBtnDisabled }}
                onClick={() => {
                  if (isCommerceOutOfStock(promoProduct.availability)) {
                    showToast(t('wpa.common.cannotOrderOutOfStock'), 3800);
                    return;
                  }
                  if (!isCatalogStatusActive(promoProduct.status)) {
                    showToast(
                      t('wpa.common.cannotOrderInactiveStatus', {
                        status: formatWpaCatalogStatusLabel(promoProduct.status, t) || '-',
                      }),
                      3800,
                    );
                  }
                }}
              >
                {(!isCatalogStatusActive(promoProduct.status) &&
                isCatalogOutOfStockStatus(promoProduct.status)
                  ? formatWpaCatalogStatusLabel(promoProduct.status, t) || '-'
                  : isCommerceOutOfStock(promoProduct.availability)
                    ? t('wpa.common.outOfStock')
                    : !isCatalogStatusActive(promoProduct.status)
                      ? formatWpaCatalogStatusLabel(promoProduct.status, t) || '-'
                      : t('wpa.home.promoOutOfStock'))}
              </button>
            ) : (
              <button type="button" style={styles.promoFullscreenBtn} onClick={handlePromoAddToCart}>
                {t('wpa.home.promoAddToCart')}
              </button>
            )}
          </div>
        </div>
      ) : null}

      <WpaAddToCartModal
        open={modalOpen}
        product={modalProduct}
        onClose={() => { setModalOpen(false); setModalProduct(null); }}
        onAddToCurrent={(qty) => finishAdd(qty, false)}
        onAddToNew={(qty) => finishAdd(qty, true)}
      />

      {toast && <div style={styles.toast}>{toast}</div>}

      {errorDialog && (
        <div style={styles.errorOverlay} role="dialog" aria-modal="true">
          <div style={styles.errorBox}>
            <div style={styles.errorTitle}>{t('wpa.common.cannotAddToCartTitle')}</div>
            <div style={styles.errorBody}>{errorDialog}</div>
            <button type="button" style={styles.errorOk} onClick={() => setErrorDialog(null)}>
              {t('wpa.cartDetail.errorOk')}
            </button>
          </div>
        </div>
      )}

      {confirmDeleteProduct && (
        <div style={styles.errorOverlay} role="dialog" aria-modal="true">
          <div style={styles.errorBox}>
            <div style={styles.errorTitle}>{t('wpa.home.recommendDeleteConfirmTitle')}</div>
            <div style={styles.errorBody}>
              {t('wpa.home.recommendDeleteConfirmMsg', { name: wpaLocalizedName(confirmDeleteProduct) })}
            </div>
            <div style={styles.confirmActions}>
              <button
                type="button"
                style={styles.confirmCancelBtn}
                onClick={() => setConfirmDeleteProduct(null)}
              >
                {t('wpa.layout.cancel')}
              </button>
              <button
                type="button"
                style={{ ...styles.confirmConfirmBtn, ...styles.confirmConfirmDanger }}
                onClick={() => {
                  const product = confirmDeleteProduct;
                  setConfirmDeleteProduct(null);
                  deleteRecommendPriceCard(product);
                }}
              >
                {t('wpa.home.recommendDeleteConfirmBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </WpaLayout>
  );
}
